import {
  SHButton,
  SHButtonProps,
  SHTypography,
} from "@components/design-systems";
import { PageRoutes } from "@constants";
import { useNotification } from "@hooks/useNotification";
import { useUserPermissions } from "@hooks/userUserPermission";
import { Link, useTheme } from "@mui/material";
import { toString } from "lodash";
import { useCallback } from "react";
import {
  generatePath,
  NavigateOptions,
  To,
  useNavigate,
} from "react-router-dom";
import Balancer from "react-wrap-balancer";

export interface LinkToOptions {
  to: To;
  options?: NavigateOptions;
  delta?: number;
  isExternal?: boolean;
}

export interface NavButtonProps extends SHButtonProps {
  linkTo: LinkToOptions;
  isActive?: boolean;
}

export const NavButton = ({
  linkTo,
  isActive,
  ...buttonProps
}: NavButtonProps): JSX.Element => {
  const navigate = useNavigate();
  const { palette } = useTheme();

  // #region IMAP Judges
  const { user, isSupplierBasic, isAdviserTrialGroup } = useUserPermissions();
  const { notify, closeNotify } = useNotification();
  const { to } = linkTo;
  const isImapUserRestricted =
    [
      PageRoutes.newsAndUpdates,
      PageRoutes.platformAnalysis,
      PageRoutes.smartSearchDev,
      PageRoutes.smartSearchStaging,
      PageRoutes.smartSearchProd,
    ].includes(toString(to)) && user?.isRestrictedUser;
  const isBasicSupplierRestricted =
    [
      PageRoutes.smartSearchDev,
      PageRoutes.smartSearchStaging,
      PageRoutes.smartSearchProd,
    ].includes(toString(to)) && isSupplierBasic;
  // #endregion

  const isAdviserTrialRestricted =
    [PageRoutes.documentLibrary].includes(toString(to)) && isAdviserTrialGroup;

  const handleOnClick = useCallback(() => {
    // #region IMAP Judges
    if (isImapUserRestricted || isBasicSupplierRestricted) {
      notify("Your account cannot view this page.", {
        variant: "error",
        close: true,
      });
      return;
    }
    // #endregion

    if (isAdviserTrialRestricted) {
      const notifyKey = "documentLibraryRetrict";

      notify(
        <SHTypography color={palette.text.white}>
          Upgrade your subscription
          <Link
            variant="body2"
            component="a"
            underline="always"
            color={palette.text.white}
            onClick={() => {
              navigate(
                `${generatePath(PageRoutes.practiceOverview, {
                  practiceId: user?.userMetadata?.adviser_firm_id,
                })}/subscription`,
              );
              closeNotify(notifyKey);
            }}
            sx={{
              paddingX: 0.5,
              cursor: "pointer",
              textDecorationColor: palette.text.white,
            }}
          >
            here
          </Link>
          to view this page.
        </SHTypography>,
        {
          variant: "error",
          close: true,
          key: notifyKey,
        },
      );
      return;
    }

    if (linkTo.isExternal) {
      window.open(toString(linkTo.to), "_blank");
      return;
    }

    if (typeof linkTo.delta === "number") {
      navigate(linkTo.delta);
    } else {
      navigate(linkTo.to, linkTo.options);
    }
  }, [
    isImapUserRestricted,
    linkTo.delta,
    linkTo.isExternal,
    linkTo.options,
    linkTo.to,
    navigate,
    notify,
    isBasicSupplierRestricted,
    isAdviserTrialRestricted,
    closeNotify,
    palette.text.white,
    user?.userMetadata?.adviser_firm_id,
  ]);

  return (
    <SHButton
      {...buttonProps}
      variant="text"
      color="inherit"
      onClick={handleOnClick}
      sx={{
        paddingX: "15px",
        minHeight: "42px",
        lineHeight: "16.8px",
        borderColor: isActive ? palette.primary.dark : "transparent",
        fontSize: { sm: "12px", md: "13px", lg: "14px" },
        fontWeight: isActive ? 600 : 500,
      }}
    >
      <Balancer>{buttonProps.children}</Balancer>
    </SHButton>
  );
};
